import React from 'react';
import { Monitor, MessageCircle, Mail, Slack, Webhook, FileSpreadsheet, Zap, Puzzle, Smartphone } from 'lucide-react';
import { WhatsAppRecipientsPicker } from './NotificationConfig/WhatsAppRecipientsPicker';
import { DiscordWebhookList } from './NotificationConfig/DiscordWebhookList';
import { WhatsAppSession } from './NotificationConfig/WhatsAppSession';
import { JobFiltersList } from './NotificationConfig/JobFiltersList';
import { NotificationTypeCard } from './NotificationConfig/NotificationTypeCard';
import { FilterScheduleModal } from './NotificationConfig/FilterScheduleModal';
import { DiscordWebhookForm } from './NotificationConfig/DiscordWebhookForm';
import { PushPermissionBox } from './NotificationConfig/PushPermissionBox';
import { GlobalScheduleCard } from './NotificationConfig/GlobalScheduleCard';
import { ConfigHeader } from './NotificationConfig/ConfigHeader';
import { useNotificationConfig } from './NotificationConfig/useNotificationConfig';

const ICONS = {
    push: <Monitor className="w-6 h-6" />,
    discord: <MessageCircle className="w-6 h-6" />,
    whatsapp: <Smartphone className="w-6 h-6" />,
    email: <Mail className="w-6 h-6" />,
    slack: <Slack className="w-6 h-6" />,
    webhook: <Webhook className="w-6 h-6" />,
    sheets: <FileSpreadsheet className="w-6 h-6" />,
    zapier: <Zap className="w-6 h-6" />,
    custom: <Puzzle className="w-6 h-6" />
};

export default function NotificationConfigView({ integration, onBack }) {
    const {
        viewMode, editingWebhookId, webhookName, webhookUrl, setWebhookName, setWebhookUrl,
        webhooks,
        permissionStatus, requestPermission,
        waSession, waSessionLoading, waGroups, waGroupsRefreshing, setWaSession, setWaGroups, fetchWaGroups,
        recipients, recipientSearch, setRecipients, setRecipientSearch,
        savedFilters, enabledFilters, activeConfigFilter, setActiveConfigFilter, toggleFilter,
        notificationType, setNotificationType,
        globalFrequency, setGlobalFrequency,
        quietHoursEnabled, setQuietHoursEnabled,
        quietHoursSlots, addTimeSlot, removeTimeSlot, updateTimeSlot,
        filterConfigs, getFilterConfig, updateFilterConfig, addFilterSlot, removeFilterSlot, updateFilterSlot,
        handleAddWebhook, handleEditWebhook, handleDeleteWebhook, handleSaveDiscordForm, sendTestNotification,
        headerTitle, handleHeaderBack,
    } = useNotificationConfig(integration, onBack);

    // Render
    return (
        <div className="max-w-[1000px] mx-auto w-full pt-8 pb-12 relative">
            {/* Header */}
            <ConfigHeader icon={ICONS[integration.id]} title={headerTitle} onBack={handleHeaderBack} />

            {/* LIST View (Discord Only) */}
            {integration.id === 'discord' && viewMode === 'list' && (
                <DiscordWebhookList
                    webhooks={webhooks}
                    onAdd={handleAddWebhook}
                    onEdit={handleEditWebhook}
                    onDelete={handleDeleteWebhook}
                />
            )}

            {/* EDIT View (Wrapper to hide if in List mode) */}
            {(integration.id !== 'discord' || viewMode === 'edit') && (
                <div className="space-y-6">

                    {/* Discord Inputs (Name/URL) inside Edit View */}
                    {integration.id === 'discord' && (
                        <DiscordWebhookForm
                            webhookName={webhookName}
                            setWebhookName={setWebhookName}
                            webhookUrl={webhookUrl}
                            setWebhookUrl={setWebhookUrl}
                        />
                    )}

                    {/* WhatsApp Sender Pairing — per-user wacli account */}
                    {integration.id === 'whatsapp' && (
                        <WhatsAppSession
                            waSession={waSession}
                            waSessionLoading={waSessionLoading}
                            setWaSession={setWaSession}
                            setWaGroups={setWaGroups}
                        />
                    )}

                    {/* WhatsApp Recipients (phone + groups, multi-select) */}
                    {integration.id === 'whatsapp' && (
                        <WhatsAppRecipientsPicker
                            recipients={recipients}
                            setRecipients={setRecipients}
                            pairedPhone={waSession?.phone_number || null}
                            groups={waGroups}
                            search={recipientSearch}
                            setSearch={setRecipientSearch}
                            onRefresh={fetchWaGroups}
                            refreshing={waGroupsRefreshing}
                        />
                    )}

                    {/* Push Permission Box */}
                    {integration.id === 'push' && (
                        <PushPermissionBox
                            permissionStatus={permissionStatus}
                            onRequestPermission={requestPermission}
                        />
                    )}


                    {/* SHARED SETTINGS (Filters, Type, Schedule) */}

                    {/* Job Filters */}
                    <JobFiltersList
                        savedFilters={savedFilters}
                        enabledFilters={enabledFilters}
                        filterConfigs={filterConfigs}
                        onToggleFilter={toggleFilter}
                        onConfigureFilter={setActiveConfigFilter}
                    />

                    {/* Notification Type */}
                    <NotificationTypeCard
                        notificationType={notificationType}
                        setNotificationType={setNotificationType}
                        onSendTest={sendTestNotification}
                    />

                    {/* Global Schedule */}
                    <GlobalScheduleCard
                        globalFrequency={globalFrequency}
                        setGlobalFrequency={setGlobalFrequency}
                        quietHoursEnabled={quietHoursEnabled}
                        setQuietHoursEnabled={setQuietHoursEnabled}
                        quietHoursSlots={quietHoursSlots}
                        addTimeSlot={addTimeSlot}
                        removeTimeSlot={removeTimeSlot}
                        updateTimeSlot={updateTimeSlot}
                    />

                    {/* SAVE BUTTON (Discord Only) */}
                    {integration.id === 'discord' && (
                        <div className="flex justify-end pt-4">
                            <button type="button" onClick={handleSaveDiscordForm} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-sm">
                                Save Webhook
                            </button>
                        </div>
                    )}

                </div>
            )}

            {/* Config Modal */}
            {activeConfigFilter && (
                <FilterScheduleModal
                    activeConfigFilter={activeConfigFilter}
                    savedFilters={savedFilters}
                    getFilterConfig={getFilterConfig}
                    updateFilterConfig={updateFilterConfig}
                    addFilterSlot={addFilterSlot}
                    removeFilterSlot={removeFilterSlot}
                    updateFilterSlot={updateFilterSlot}
                    onClose={() => setActiveConfigFilter(null)}
                />
            )}
        </div>
    );
}
